import { useEffect, useState } from 'react';

import type { Post } from '~/interfaces/post';

import { useNonRevalidatingFetcher } from '../useNonRevalidatingFetcher';
import type { PostLike } from './usePostInsights';

export type PostInsight = {
  id: string;
  likes: PostLike[];
  comment_count: number;
};

interface PostWithInsights extends Omit<Post, 'likes'> {
  id: string;
  likes: PostLike[];
  comment_count: number;
}

interface Props {
  incomingPosts: Post[];
}

interface ReturnProps {
  postsWithInsights: PostWithInsights[];
}

const usePostsInsights = ({ incomingPosts }: Props): ReturnProps => {
  const fetcher = useNonRevalidatingFetcher<() => Promise<ReturnProps>>();
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const postIds = incomingPosts?.map((post) => post.id) || [];
    const hasPostIds = postIds && postIds.length > 0;

    if (hasPostIds && !hasFetched) {
      // send the post ids as JSON
      fetcher.submit(JSON.stringify({ post_ids: postIds }), {
        method: 'post',
        action: '/posts_insights',
        encType: 'application/json',
      });
      setHasFetched(true);
    }
  }, [incomingPosts, hasFetched, fetcher]);

  const insights = fetcher.data as PostInsight[] | undefined;

  return {
    postsWithInsights: incomingPosts?.map((post) => {
      const postInsight = insights?.find((insight: PostInsight) => insight.id === post.id);
      return {
        ...post,
        likes: postInsight ? postInsight.likes.filter((l: PostLike) => l.liked) : [],
        comment_count: postInsight ? postInsight.comment_count : 0,
      };
    }),
  };
};

export default usePostsInsights;
